import React, { useContext, useMemo, useState, useEffect } from "react";
import ReactSlider from "react-slider";
import MyContext from "../MyContext/MyContext";

const ShopSidebar = ({ active, sidebarController, onFilter }) => {
  const { data } = useContext(MyContext);

  // Flatten products
  const allProducts = useMemo(
    () => (data || []).flatMap((cat) => cat.products || []),
    [data]
  );

  // Categories
  const categories = useMemo(
    () => data.map((c) => ({ name: c.category, count: c.products.length })),
    [data]
  );

  // Brands
  const brands = useMemo(() => {
    const list = allProducts.map((p) => p.details?.Brand).filter(Boolean);
    const unique = [...new Set(list)];
    return unique.map((b) => ({
      name: b,
      count: allProducts.filter((p) => p.details?.Brand === b).length,
    }));
  }, [allProducts]);

  // Colors
  const colors = useMemo(() => {
    const list = allProducts.flatMap((p) => p.colors || []);
    const unique = [...new Set(list)];
    return unique.map((c) => ({
      name: c,
      count: allProducts.filter((p) => p.colors?.includes(c)).length,
    }));
  }, [allProducts]);

  // Price range
  const priceRange = useMemo(() => {
    const prices = allProducts.map((p) => p.price.current);
    return [Math.min(...prices), Math.max(...prices)];
  }, [allProducts]);

  const [filterPrice, setFilterPrice] = useState(priceRange);
  const [selectedRating, setSelectedRating] = useState("");
  const [selectedColor, setSelectedColor] = useState("");
  const [selectedBrand, setSelectedBrand] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");

  // ⭐ FIX: Add onFilter to dependency list
  useEffect(() => {
    onFilter({
      price: filterPrice,
      rating: selectedRating,
      color: selectedColor,
      brand: selectedBrand,
      category: selectedCategory,
    });
  }, [
    filterPrice,
    selectedRating,
    selectedColor,
    selectedBrand,
    selectedCategory,
    onFilter, // ⭐ REQUIRED to remove the warning
  ]);

  return (
    <div className="col-lg-3">

      {/* INLINE CSS — COMPLETE PROFESSIONAL UI */}
      <style>{`
        .shop-sidebar__box {
          border: 1px solid #e5e5e5;
          border-radius: 10px;
          padding: 22px;
          background: #ffffff;
          box-shadow: 0 2px 8px rgba(0,0,0,0.06);
          transition: 0.25s ease;
        }

        .shop-sidebar__box:hover {
          box-shadow: 0 4px 16px rgba(0,0,0,0.12);
        }

        .shop-sidebar__box h6 {
          font-size: 17px;
          font-weight: 600;
          margin-bottom: 14px;
        }

        .form-select {
          width: 100%;
          padding: 10px 14px;
          border-radius: 8px;
          border: 1px solid #dcdcdc;
          background: #fafafa;
          font-size: 15px;
          transition: 0.2s ease;
        }

        .form-select:hover {
          border-color: #b30000;
          background: #fff;
        }

        /* PRICE SLIDER FIX */
        .price-slider {
          width: 100%;
          height: 6px;
          margin-top: 10px;
          background-color: #b30000;
        }

        .price-track {
          background: #e4e4e4;
          height: 6px;
          border-radius: 50px;
        }

        .price-track-active {
          background: linear-gradient(90deg, #b30000, #ff4d4d);
          height: 6px;
          border-radius: 50px;
          transition: background 0.15s ease;
        }

        .price-thumb {
          height: 18px;
          width: 18px;
          background: white;
          border-radius: 50%;
          border: 2px solid #b30000;
          cursor: grab;
          margin-top: -6px;
          box-shadow: 0 4px 8px rgba(0,0,0,0.22);
          transition: transform 0.15s ease, box-shadow 0.15s ease;
        }

        .price-thumb:hover {
          transform: scale(1.12);
          box-shadow: 0 6px 14px rgba(0,0,0,0.24);
        }

        .price-thumb:active {
          cursor: grabbing;
          transform: scale(1.18);
        }
      `}</style>

      <div className={`shop-sidebar ${active && "active"}`}>

        {/* MOBILE CLOSE BUTTON */}
        <button
          onClick={sidebarController}
          type="button"
          className="shop-sidebar__close d-lg-none d-flex"
        >
          <i className="ph ph-x" />
        </button>

        {/* CATEGORY */}
        <div className="shop-sidebar__box mb-32">
          <h6>Product Category</h6>
          <select
            className="form-select"
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
          >
            <option value="">All Categories</option>
            {categories.map((cat, i) => (
              <option key={i} value={cat.name}>
                {cat.name} ({cat.count})
              </option>
            ))}
          </select>
        </div>

        {/* PRICE RANGE */}
        <div className="shop-sidebar__box mb-32">
          <h6>Filter by Price</h6>

          <ReactSlider
            className="price-slider"
            thumbClassName="price-thumb"
            trackClassName={(i) =>
              i === 1 ? "price-track-active" : "price-track"
            }
            value={filterPrice}
            min={priceRange[0]}
            max={priceRange[1]}
            pearling
            minDistance={10}
            onChange={setFilterPrice}
          />

          <p className="mt-3 fw-semibold">
            ${filterPrice[0]} — ${filterPrice[1]}
          </p>
        </div>

        {/* RATING */}
        <div className="shop-sidebar__box mb-32">
          <h6>Filter by Rating</h6>
          <select
            className="form-select"
            value={selectedRating}
            onChange={(e) => setSelectedRating(e.target.value)}
          >
            <option value="">Any Rating</option>
            {[5, 4, 3, 2, 1].map((stars) => (
              <option key={stars} value={stars}>
                {stars}★ & Up
              </option>
            ))}
          </select>
        </div>

        {/* COLOR */}
        <div className="shop-sidebar__box mb-32">
          <h6>Filter by Color</h6>
          <select
            className="form-select"
            value={selectedColor}
            onChange={(e) => setSelectedColor(e.target.value)}
          >
            <option value="">All Colors</option>
            {colors.map((c, i) => (
              <option key={i} value={c.name}>
                {c.name} ({c.count})
              </option>
            ))}
          </select>
        </div>

        {/* BRAND */}
        <div className="shop-sidebar__box mb-32">
          <h6>Filter by Brand</h6>
          <select
            className="form-select"
            value={selectedBrand}
            onChange={(e) => setSelectedBrand(e.target.value)}
          >
            <option value="">All Brands</option>
            {brands.map((b, i) => (
              <option key={i} value={b.name}>
                {b.name} ({b.count})
              </option>
            ))}
          </select>
        </div>

        {/* AD IMAGE */}
        <div className="shop-sidebar__box">
          <img
            src="assets/images/thumbs/advertise-img1.png"
            alt="advert"
            style={{
              width: "100%",
              borderRadius: "10px",
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default ShopSidebar;
